import * as R from 'remeda';
import { z } from 'zod';
import {
  GridFilterItem,
  GridFilterModel,
  GridLogicOperator,
} from '@rossum/ui/x-data-grid-pro';
import { CodecFieldTypes, dataGridOperatorsMap } from './constants';
import { isEmptyValue } from './helpers';
import { encodeValue, inferFieldType } from './transformers';
import { ApiConditionModel, ColumnEncoder, OperatorEncoder } from './types';

type DataGridOperator = GridFilterItem['operator'];

// filtering model is validated in the component level, this schema is not used in the request in case of invalid shape
const filteringItemSchema = z.object({
  id: z.union([z.string(), z.number()]).optional(),
  field: z.string(),
  value: z.any().nullable().optional(),
  operator: z.string(),
});

export const filteringSchema = z.object({
  items: z.array(filteringItemSchema),
  logicOperator: z
    .union([z.literal(GridLogicOperator.And), z.literal(GridLogicOperator.Or)])
    .optional(),
});

type FilteringSchema = z.infer<typeof filteringSchema>;

export type DataGridConditionModel = GridFilterModel extends FilteringSchema
  ? GridFilterModel
  : never;

export const encodeMqlQuery = (
  filterItems: GridFilterItem[],
  options?: {
    codecFieldTypes?: CodecFieldTypes;
    columnEncoders?: Record<string, ColumnEncoder>;
    operatorEncoders?: Partial<Record<DataGridOperator, OperatorEncoder>>;
  }
): ApiConditionModel => {
  const operatorEncoders: Partial<Record<string, OperatorEncoder>> =
    options?.operatorEncoders ?? dataGridOperatorsMap;

  const conditions = filterItems.flatMap(item => {
    const { field, operator, value } = item;

    if (!field || !operator || isEmptyValue(value)) {
      return null;
    }

    const operatorEncoder =
      operator in operatorEncoders ? operatorEncoders[operator] : null;

    if (!operatorEncoder) return null;

    const columnEncoder = options?.columnEncoders?.[field];
    const fieldType =
      options?.codecFieldTypes?.[field] ?? inferFieldType(field);
    const coercedValue = encodeValue(value, {
      field,
      codecFieldType: fieldType,
    });

    return columnEncoder
      ? columnEncoder({ operator, value: coercedValue })
      : operatorEncoder(field, coercedValue);
  });

  const filteredConditions = R.pipe(conditions, R.filter(R.isTruthy)) as Record<
    string,
    unknown
  >[];

  return {
    // we do not support dynamic logic operator eg. `$${dataGridCondition?.logicOperator}`, only $and for now
    // we need to solve exclusion of statuses with $nin for $or in order to support dynamic operators
    $and: filteredConditions,
  };
};
